import { getErrorMessage } from "@/utils/getErrorMessage";
import { makeRequest } from "@/utils/makeRequest";
import {
  ImageKitAbortError,
  ImageKitInvalidRequestError,
  ImageKitServerError,
  ImageKitUploadNetworkError,
  upload,
} from "@imagekit/react";
import { toast } from "sonner";

export interface UploadResult {
  url: string;
  fileId: string;
  name: string;
}

interface UploadOptions {
  files: File | File[];
  onProgress?: (progress: number, index?: number) => void;
  abortSignal?: AbortSignal;
  folder?: string;
  fileNamePrefix?: string;
}

/**
 * Fetch ImageKit auth parameters from backend
 */
const authenticate = async () => {
  try {
    const res = await makeRequest.get("/utils/imagekit-auth");
    return res.data as {
      signature: string;
      expire: number;
      token: string;
      publicKey: string;
    };
  } catch (error) {
    toast.error(getErrorMessage(error));
    throw error;
  }
};

/**
 * Reusable ImageKit upload function (single or multiple)
 */
export async function uploadFiles(
  options: UploadOptions,
): Promise<UploadResult[]> {
  const { files, onProgress, abortSignal, fileNamePrefix, folder } = options;

  const fileArray = Array.isArray(files) ? files : [files];

  try {
    const uploads = fileArray.map(async (file, index) => {
      // ✅ NEW TOKEN PER FILE
      const { signature, expire, token, publicKey } = await authenticate();

      const response = await upload({
        file,
        fileName: fileNamePrefix
          ? `${fileNamePrefix}-${Date.now()}-${index}-${file.name}`
          : `${Date.now()}-${index}-${file.name}`,
        publicKey,
        expire,
        token,
        signature,
        folder,
        abortSignal,
        onProgress: (event) => {
          if (onProgress && event.total) {
            const percent = (event.loaded / event.total) * 100;
            onProgress(percent, index);
          }
        },
      });

      if (!response.url || !response.fileId || !response.name) {
        throw new Error("Invalid upload response from ImageKit");
      }

      return {
        url: response.url,
        fileId: response.fileId,
        name: response.name,
      };
    });

    return await Promise.all(uploads);
  } catch (error) {
    if (error instanceof ImageKitAbortError) {
      throw new Error("Upload aborted");
    }
    if (error instanceof ImageKitInvalidRequestError) {
      throw new Error(error.message);
    }
    if (error instanceof ImageKitUploadNetworkError) {
      throw new Error("Network error during upload");
    }
    if (error instanceof ImageKitServerError) {
      throw new Error("ImageKit server error");
    }
    throw error;
  }
}
